import React, { useState, useCallback, forwardRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useCombobox } from 'downshift';
import { useFormikContext } from 'formik';

import { SearchField } from './styles';

import { fetchAutoSuggestResults } from '../search/helpers';

const SearchInput = forwardRef(
  ({ placeholder, name, id, section, inHeader, ...rest }, ref) => {
    const [autoSuggestions, setAutoSuggestions] = useState([]);
    const [query, setQuery] = useState(null);
    const { submitForm, setFieldValue } = useFormikContext();

    // Check if there is a query in the URL and set it as the initial value
    useEffect(() => {
      const urlParams = new URLSearchParams(window.location.search);
      if (typeof window === 'undefined') return;
      const queryParam = urlParams.get('query');
      if (!queryParam) return;
      setQuery(queryParam);
    }, []);

    const onInputValueChange = useCallback(
      async ({ inputValue, selectedItem, type }) => {
        if (
          type === useCombobox.stateChangeTypes.ItemClick ||
          type === useCombobox.stateChangeTypes.InputKeyDownEnter
        ) {
          setFieldValue(name, selectedItem);
          submitForm();
        } else {
          setFieldValue(name, inputValue);
          setQuery(inputValue);
          if (inputValue.length > 1) {
            setAutoSuggestions(
              (await fetchAutoSuggestResults(inputValue, section)).slice(0, 6)
            );
          } else {
            setAutoSuggestions([]);
          }
        }
      },
      [submitForm, setFieldValue, section, name]
    );

    const getA11yStatusMessage = useCallback(({ isOpen, resultCount }) => {
      if (!isOpen) {
        return '';
      }

      if (!resultCount) {
        return 'No results are available.';
      }

      if (resultCount) {
        return `${resultCount} result${
          resultCount === 1 ? ' is' : 's are'
        } available, use up and down arrow keys to navigate. Press Enter key to select.`;
      }

      return '';
    }, []);

    // this filters our current list of suggestions before our callback is even made
    // which improves perceived responsiveness
    const filteredSuggestions = autoSuggestions.filter(
      (suggestion) =>
        suggestion.toLowerCase().indexOf(query.toLowerCase()) === 0
    );

    const onKeyDown = useCallback(
      (event) => {
        if (event.key === 'Enter') {
          submitForm();
        }
      },
      [submitForm]
    );

    return (
      <SearchField
        name={name}
        id={id}
        type="search"
        value={!inHeader && query?.length > 1 ? query : null}
        items={(query?.length > 1 && filteredSuggestions) || []}
        placeholder={placeholder}
        onInputValueChange={onInputValueChange}
        getA11yStatusMessage={getA11yStatusMessage}
        onKeyDown={onKeyDown}
        suggestionHeading="Suggestions"
        suggestionPrefixIcon="search"
        ref={ref}
        {...rest}
      />
    );
  }
);

SearchInput.displayName = 'SearchInput';

SearchInput.propTypes = {
  placeholder: PropTypes.string,
  name: PropTypes.string,
  id: PropTypes.string,
  section: PropTypes.string,
  inHeader: PropTypes.bool,
};

export default SearchInput;
